// Auto-extracted tab: governance
import React, { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * GovernanceTab - extracted from app.jsx
 * Original lines: 7410-7804
 * 
 * TODO: This component needs props passed from AppContent:
 * - darkMode, scenarios, thresholds, etc.
 * - All the state and handlers it uses
 */
export function GovernanceTab(props) {
  // Original render content (needs refactoring to use props):
  return (
    <>
          <div className="space-y-6">
            <Card
              title="Scenarios"
              subtitle={`${scenarios.length} monitoring scenarios${Object.values(scenarioFilters).some(v => v !== 'all') ? ' (filtered)' : ''}`}
              actions={!examinerModeActive && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowBulkCSVImport({ open: true, entityType: 'scenario' })}
                    disabled={!orgId}
                  >
                    📥 CSV Import
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setImportWizardModal(true)}
                    disabled={!orgId}
                  >
                    📄 AI Import
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setInterviewModal(true)}
                    disabled={!orgId}
                  >
                    + Guided
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openScenarioCreate}
                    disabled={!orgId}
                  >
                    + Expert
                  </Button>
                </div>
              )}
            >
              {/* Filter Bar */}
              <TableFilterBar
                filters={scenarioFilters}
                onChange={setScenarioFilters}
                filterOptions={[
                  { key: 'risk', label: 'Risk', options: [
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' },
                  ]},
                  { key: 'status', label: 'Review Status', options: [
                    { value: 'overdue', label: 'Overdue' },
                    { value: 'due_soon', label: 'Due Soon (30d)' },
                    { value: 'current', label: 'Current' },
                  ]},
                ]}
                darkMode={darkMode}
              />
              <div className="overflow-x-auto rounded-b-xl border border-t-0 border-slate-200/50">
                <table className="w-full text-sm">
                  <thead className={`${darkMode ? "bg-slate-800" : "bg-gradient-to-r from-slate-50 to-slate-100/80"}`}>
                    <tr>
                      {!examinerModeActive && (
                        <th className="px-2 py-3.5 w-10">
                          <input
                            type="checkbox"
                            aria-label="Select all scenarios"
                            checked={scenarios.length > 0 && bulkSelection.scenarios.length === scenarios.length}
                            onChange={(e) => setBulkSelection(prev => ({
                              ...prev,
                              scenarios: e.target.checked ? scenarios.map(s => s.id) : []
                            }))}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                        </th>
                      )}
                      <SortableHeader label="Name" field="name" sortState={scenarioSort} onSort={(f) => setScenarioSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <SortableHeader label="Owner" field="owner_email" sortState={scenarioSort} onSort={(f) => setScenarioSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <SortableHeader label="Risk" field="risk_level" sortState={scenarioSort} onSort={(f) => setScenarioSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <th className={`px-4 py-3.5 text-left font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>EQ Score</th>
                      <SortableHeader label="Next Review" field="next_review_date" sortState={scenarioSort} onSort={(f) => setScenarioSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <th className={`px-4 py-3.5 text-right font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? "divide-slate-700 bg-slate-900" : "divide-slate-100 bg-white"}`}>
                    {dataLoading ? (
                      <tr><td colSpan={examinerModeActive ? 6 : 7} className="px-4 py-16">
                        <div className="flex flex-col items-center justify-center">
                          <Icons.Refresh className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                          <div className="text-slate-600 font-medium">Loading scenarios...</div>
                        </div>
                      </td></tr>
                    ) : scenarios.length === 0 ? (
                      <tr><td colSpan={6} className="p-4">
                        <EducationalEmptyState
                          title="No monitoring scenarios yet"
                          examinerSays="Show me your documented monitoring scenarios. How do you detect structuring? Wire fraud? Unusual activity? What thresholds trigger alerts?"
                          example="Cash Structuring Detection - Monitors for customers breaking large transactions into smaller amounts to avoid CTR filing. Alert threshold: $9,000+ aggregate over 7 days from same customer."
                          templates={[
                            { name: "Cash Structuring Detection", type: "cash_structuring" },
                            { name: "Wire Transfer Monitoring", type: "wire_monitoring" },
                            { name: "SAR Filing Process", type: "sar_filing" },
                            { name: "CDD/KYC Procedures", type: "cdd_kyc" },
                            { name: "Funnel Account Detection", type: "funnel_account" },
                          ]}
                          onAddBlank={() => { setNewScenarioTemplate(null); setInterviewModal(true); }}
                          onSelectTemplate={(t) => {
                            setNewScenarioTemplate(t.type);
                            setInterviewModal(true);
                          }}
                          darkMode={darkMode}
                        />
                      </td></tr>
                    ) : scenarios
                      // Apply filters
                      .filter(s => {
                        if (scenarioFilters.risk !== 'all' && s.risk_level !== scenarioFilters.risk) return false;
                        if (scenarioFilters.status !== 'all') {
                          const reviewDate = s.next_review_date ? new Date(s.next_review_date) : null;
                          const now = new Date();
                          const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                          if (scenarioFilters.status === 'overdue' && (!reviewDate || reviewDate >= now)) return false;
                          if (scenarioFilters.status === 'due_soon' && (!reviewDate || reviewDate < now || reviewDate > in30Days)) return false;
                          if (scenarioFilters.status === 'current' && (reviewDate && reviewDate < now)) return false;
                        }
                        return true;
                      })
                      // Apply sorting
                      .sort((a, b) => {
                        const { field, direction } = scenarioSort;
                        let aVal = a[field] || '';
                        let bVal = b[field] || '';
                        if (field === 'next_review_date') {
                          aVal = aVal ? new Date(aVal).getTime() : 0;
                          bVal = bVal ? new Date(bVal).getTime() : 0;
                        }
                        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                        return 0;
                      })
                      .map(s => {
                      const key = getRigorKey("scenario", s.id);
                      const result = rigorResults[key];
                      const isSelected = bulkSelection.scenarios.includes(s.id);
                      return (
                      <tr key={s.id} className={`transition-colors group ${isSelected ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') : (darkMode ? 'hover:bg-slate-800' : 'hover:bg-blue-50/50')}`}>
                        {!examinerModeActive && (
                          <td className="px-2 py-3.5">
                            <input
                              type="checkbox"
                              aria-label={`Select ${s.name}`}
                              checked={isSelected}
                              onChange={(e) => setBulkSelection(prev => ({
                                ...prev,
                                scenarios: e.target.checked 
                                  ? [...prev.scenarios, s.id]
                                  : prev.scenarios.filter(id => id !== s.id)
                              }))}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3.5">
                          <button 
                            onClick={() => setEqDetailModal({ open: true, item: s, type: "scenario" })}
                            className={`font-medium transition-colors text-left group-hover:text-blue-600 ${darkMode ? 'text-slate-100 hover:text-blue-400' : 'text-slate-900 hover:text-blue-600'}`}
                          >
                            {s.name}
                          </button>
                        </td>
                        <td className={`px-4 py-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>{s.owner_email || "—"}</td>
                        <td className="px-4 py-3.5">
                          <Badge color={s.risk_level === 'high' ? 'red' : s.risk_level === 'medium' ? 'amber' : 'emerald'}>
                            {s.risk_level || 'low'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3.5"><EQScoreBadge item={s} /></td>
                        <td className={`px-4 py-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                          {s.next_review_date && new Date(s.next_review_date) < new Date() 
                            ? <span className="text-red-500 font-medium">⚠ {formatDate(s.next_review_date)}</span>
                            : formatDate(s.next_review_date)}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" onClick={() => setEqDetailModal({ open: true, item: s, type: "scenario" })}>
                              View 8Q
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => runRigorCheck("scenario", s)} disabled={rigorCheckingKey === key || rigorBatchRunning}>
                              {rigorCheckingKey === key ? "Checking..." : "Rigor"}
                            </Button>
                            {!examinerModeActive && <Button variant="ghost" size="sm" onClick={() => openScenarioEdit(s)}>Edit</Button>}
                            {!examinerModeActive && <Button variant="ghost" size="sm" onClick={() => openDeleteModal("scenario", s)}>Delete</Button>}
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card
              title="Thresholds"
              subtitle={`${thresholds.length} monitoring thresholds${Object.values(thresholdFilters).some(v => v !== 'all') ? ' (filtered)' : ''}`}
              actions={!examinerModeActive && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowBulkCSVImport({ open: true, entityType: 'threshold' })}
                    disabled={!orgId}
                  >
                    📥 CSV Import
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openThresholdCreate}
                    disabled={!orgId}
                  >
                    + New Threshold
                  </Button>
                </div>
              )}
            >
              {/* Filter Bar */}
              <TableFilterBar
                filters={thresholdFilters}
                onChange={setThresholdFilters}
                filterOptions={[
                  { key: 'risk', label: 'Risk', options: [
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' },
                  ]},
                  { key: 'status', label: 'Review Status', options: [
                    { value: 'overdue', label: 'Overdue' },
                    { value: 'due_soon', label: 'Due Soon (30d)' },
                    { value: 'current', label: 'Current' },
                  ]},
                ]}
                darkMode={darkMode}
              />
              <div className="overflow-x-auto rounded-b-xl border border-t-0 border-slate-200/50">
                <table className="w-full text-sm">
                  <thead className={`${darkMode ? "bg-slate-800" : "bg-gradient-to-r from-slate-50 to-slate-100/80"}`}>
                    <tr>
                      {!examinerModeActive && (
                        <th className="px-2 py-3.5 w-10">
                          <input
                            type="checkbox"
                            aria-label="Select all thresholds"
                            checked={thresholds.length > 0 && bulkSelection.thresholds.length === thresholds.length}
                            onChange={(e) => setBulkSelection(prev => ({
                              ...prev,
                              thresholds: e.target.checked ? thresholds.map(t => t.id) : []
                            }))}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                        </th>
                      )}
                      <SortableHeader label="Name" field="name" sortState={thresholdSort} onSort={(f) => setThresholdSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <SortableHeader label="Value" field="current_value" sortState={thresholdSort} onSort={(f) => setThresholdSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <SortableHeader label="Risk" field="risk_level" sortState={thresholdSort} onSort={(f) => setThresholdSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <th className={`px-4 py-3.5 text-left font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>EQ Score</th>
                      <SortableHeader label="Next Review" field="next_review_date" sortState={thresholdSort} onSort={(f) => setThresholdSort(prev => ({ field: f, direction: prev.field === f && prev.direction === 'asc' ? 'desc' : 'asc' }))} darkMode={darkMode} />
                      <th className={`px-4 py-3.5 text-right font-semibold ${darkMode ? "text-slate-300" : "text-slate-700"}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? "divide-slate-700 bg-slate-900" : "divide-slate-100 bg-white"}`}>
                    {dataLoading ? (
                      <tr><td colSpan={examinerModeActive ? 6 : 7} className="px-4 py-16">
                        <div className="flex flex-col items-center justify-center">
                          <Icons.Refresh className="w-8 h-8 text-indigo-500 animate-spin mb-3" />
                          <div className={`font-medium ${darkMode ? "text-slate-300" : "text-slate-600"}`}>Loading thresholds...</div>
                        </div>
                      </td></tr>
                    ) : thresholds.length === 0 ? (
                      <tr><td colSpan={examinerModeActive ? 6 : 7} className="p-4">
                        <EducationalEmptyState
                          title="No thresholds documented yet"
                          examinerSays="Walk me through your monitoring thresholds. Why $3,000 for wires? Why 10 transactions in 30 days? Show me the data analysis behind these decisions."
                          example="Cash Structuring Threshold - $9,000 aggregate over 7 business days. Based on analysis of 18 months of transaction data showing this captures 95% of true structuring patterns while minimizing false positives."
                          templates={[
                            { name: "Cash Structuring Threshold", type: "cash_structuring" },
                            { name: "Wire Transfer Alert Threshold", type: "wire_monitoring" },
                            { name: "Wire Volume Threshold", type: "wire_volume" },
                            { name: "Cash-Intensive Business Threshold", type: "cash_intensive" },
                            { name: "Rapid Movement Threshold", type: "rapid_movement" },
                          ]}
                          onAddBlank={openThresholdCreate}
                          onSelectTemplate={(t) => {
                            openThresholdCreate(t.type);
                          }}
                          darkMode={darkMode}
                        />
                      </td></tr>
                    ) : thresholds
                      // Apply filters
                      .filter(t => {
                        if (thresholdFilters.risk !== 'all' && t.risk_level !== thresholdFilters.risk) return false;
                        if (thresholdFilters.status !== 'all') {
                          const reviewDate = t.next_review_date ? new Date(t.next_review_date) : null;
                          const now = new Date();
                          const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                          if (thresholdFilters.status === 'overdue' && (!reviewDate || reviewDate >= now)) return false;
                          if (thresholdFilters.status === 'due_soon' && (!reviewDate || reviewDate < now || reviewDate > in30Days)) return false;
                          if (thresholdFilters.status === 'current' && (reviewDate && reviewDate < now)) return false;
                        }
                        return true;
                      })
                      // Apply sorting
                      .sort((a, b) => {
                        const { field, direction } = thresholdSort;
                        let aVal = a[field] || '';
                        let bVal = b[field] || '';
                        if (field === 'next_review_date') {
                          aVal = aVal ? new Date(aVal).getTime() : 0;
                          bVal = bVal ? new Date(bVal).getTime() : 0;
                        }
                        if (field === 'current_value') {
                          aVal = parseFloat(aVal) || 0;
                          bVal = parseFloat(bVal) || 0;
                        }
                        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                        return 0;
                      })
                      .map(t => {
                      const key = getRigorKey("threshold", t.id);
                      const result = rigorResults[key];
                      const isSelected = bulkSelection.thresholds.includes(t.id);
                      return (
                      <tr key={t.id} className={`transition-colors group ${isSelected ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') : (darkMode ? 'hover:bg-slate-800' : 'hover:bg-blue-50/50')}`}>
                        {!examinerModeActive && (
                          <td className="px-2 py-3.5">
                            <input
                              type="checkbox"
                              aria-label={`Select ${t.name}`}
                              checked={isSelected}
                              onChange={(e) => setBulkSelection(prev => ({
                                ...prev,
                                thresholds: e.target.checked 
                                  ? [...prev.thresholds, t.id]
                                  : prev.thresholds.filter(id => id !== t.id)
                              }))}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3.5">
                          <button 
                            onClick={() => setEqDetailModal({ open: true, item: t, type: "threshold" })}
                            className={`font-medium transition-colors text-left group-hover:text-blue-600 ${darkMode ? 'text-slate-100 hover:text-blue-400' : 'text-slate-900 hover:text-blue-600'}`}
                          >
                            {t.name}
                          </button>
                        </td>
                        <td className={`px-4 py-3.5 font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{t.current_value ?? "—"} {t.unit || ""}</td>
                        <td className="px-4 py-3.5">
                          <Badge color={t.risk_level === 'high' ? 'red' : t.risk_level === 'medium' ? 'amber' : 'emerald'}>
                            {t.risk_level || 'low'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3.5"><EQScoreBadge item={t} /></td>
                        <td className={`px-4 py-3.5 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                          {t.next_review_date && new Date(t.next_review_date) < new Date() 
                            ? <span className="text-red-500 font-medium">⚠ {formatDate(t.next_review_date)}</span>
                            : formatDate(t.next_review_date)}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" onClick={() => setEqDetailModal({ open: true, item: t, type: "threshold" })}>
                              View 8Q
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => runRigorCheck("threshold", t)} disabled={rigorCheckingKey === key || rigorBatchRunning}>
                              {rigorCheckingKey === key ? "Checking..." : "Rigor"}
                            </Button>
                            {!examinerModeActive && <Button variant="ghost" size="sm" onClick={() => openThresholdEdit(t)}>Edit</Button>}
                            {!examinerModeActive && <Button variant="ghost" size="sm" onClick={() => openDeleteModal("threshold", t)}>Delete</Button>}
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
    </>
  );
}

export default GovernanceTab;
